import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { Component, ReactNode } from 'react';

import { ErrorPage } from '@/components/errors';
import { useUIStore } from '@/store/useUI';

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

if (!clerkPubKey) {
  const errorMessage =
    'Missing Clerk Publishable Key. ' +
    'Please set VITE_CLERK_PUBLISHABLE_KEY environment variable. ' +
    'For Cloudflare Pages: Settings → Environment Variables → Add for Production and Preview environments.';
  throw new Error(errorMessage);
}

/**
 * Error boundary for Clerk Provider
 * Catches Clerk initialization and runtime errors
 */
class ClerkErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('Clerk Error:', error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage status="clerk" />;
    }

    return this.props.children;
  }
}

/**
 * Get the resolved theme (light or dark) based on current setting
 */
function getResolvedTheme(theme: 'light' | 'dark' | 'system'): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

/**
 * Inner provider that uses the theme store for reactive dark mode
 */
function ClerkProviderWithTheme({ children }: { children: ReactNode }) {
  const theme = useUIStore((state) => state.theme);
  const resolvedTheme = getResolvedTheme(theme);

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      appearance={{
        baseTheme: resolvedTheme === 'dark' ? dark : undefined,
      }}
    >
      {children}
    </ClerkProvider>
  );
}

export function ClerkProviderWrapper({ children }: { children: ReactNode }) {
  return (
    <ClerkErrorBoundary>
      <ClerkProviderWithTheme>{children}</ClerkProviderWithTheme>
    </ClerkErrorBoundary>
  );
}

export { useAuth };
